import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";

const ContractStatusChart = ({ data }) => {
  const [viewMode, setViewMode] = useState("global"); // 'global' ou 'agency'

  if (!data || !data.global) {
    return (
      <div className="bg-white shadow rounded-lg p-5">
        <h3 className="text-lg font-semibold mb-4">État des contrats</h3>
        <div className="h-80 flex items-center justify-center">
          <p className="text-gray-500">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  // Trier les données globales par ordre croissant de valeur
  const sortedGlobalData = [...data.global].sort((a, b) => a.value - b.value);

  // Transformer les données pour le graphique par agence
  const prepareAgencyData = () => {
    // Extraire la liste des agences
    const agencies = data.byAgency.map((agency) => agency.agency);

    // Pour chaque type de contrat, créer une série
    const series = [
      {
        name: "Contrats en cours",
        color: "#4F46E5",
        dataKey: "inProgress",
      },
      {
        name: "En cours avec dépassement",
        color: "#EF4444",
        dataKey: "inProgressOverdue",
      },
      {
        name: "Rachetés avec remise",
        color: "#10B981",
        dataKey: "redeemedWithDiscount",
      },
      {
        name: "Rachetés sans remise",
        color: "#F59E0B",
        dataKey: "redeemedWithoutDiscount",
      },
    ];

    // Calculer le total pour chaque agence pour les pourcentages
    const agencyTotals = data.byAgency.map((agency) => {
      const total =
        agency.inProgress +
        agency.inProgressOverdue +
        agency.redeemedWithDiscount +
        agency.redeemedWithoutDiscount;
      return {
        agency: agency.agency,
        total,
      };
    });

    return { agencies, series, agencyTotals };
  };

  const { series } = prepareAgencyData();

  // Calculer le total pour chaque catégorie
  const totals = {
    inProgress: data.byAgency.reduce(
      (sum, agency) => sum + agency.inProgress,
      0
    ),
    inProgressOverdue: data.byAgency.reduce(
      (sum, agency) => sum + agency.inProgressOverdue,
      0
    ),
    redeemedWithDiscount: data.byAgency.reduce(
      (sum, agency) => sum + agency.redeemedWithDiscount,
      0
    ),
    redeemedWithoutDiscount: data.byAgency.reduce(
      (sum, agency) => sum + agency.redeemedWithoutDiscount,
      0
    ),
  };

  const totalContracts = Object.values(totals).reduce(
    (sum, value) => sum + value,
    0
  );

  // Tooltip personnalisé pour le graphique en barres
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-md rounded border border-gray-200">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <div
              key={index}
              className="flex justify-between items-center gap-2"
            >
              <div className="flex items-center">
                <div
                  className="w-3 h-3 mr-2"
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span>{entry.name}:</span>
              </div>
              <span className="font-medium" style={{ color: entry.color }}>
                {entry.value} contrats
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Tooltip personnalisé pour le graphique en secteurs
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / totalContracts) * 100).toFixed(1);
      return (
        <div className="bg-white p-3 shadow-md rounded border border-gray-200">
          <p className="font-medium">{payload[0].name}</p>
          <div className="flex justify-between mt-1">
            <span>Nombre:</span>
            <span className="font-medium">{payload[0].value} contrats</span>
          </div>
          <div className="flex justify-between">
            <span>Proportion:</span>
            <span className="font-medium">{percentage}%</span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Rendu spécifique pour la vue par agence
  const renderAgencyView = () => {
    return (
      <div className="space-y-6">
        {/* Graphique à barres groupées par agence */}
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data.byAgency}
              margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
              barCategoryGap="10%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="agency"
                angle={0}
                textAnchor="middle"
                height={60}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `Agence ${value}`}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value) =>
                  value > 999 ? `${(value / 1000).toFixed(1)}k` : value
                }
              />
              <Tooltip content={<CustomBarTooltip />} />
              <Legend wrapperStyle={{ paddingTop: 15 }} iconType="circle" />
              {series.map((s) => (
                <Bar
                  key={s.name}
                  dataKey={s.dataKey}
                  name={s.name}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={50}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Graphique de répartition proportionnelle par agence */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Répartition des contrats par agence
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.byAgency.map((agency) => {
              const total =
                agency.inProgress +
                agency.inProgressOverdue +
                agency.redeemedWithDiscount +
                agency.redeemedWithoutDiscount;
              if (total === 0) return null;

              return (
                <div
                  key={agency.agency}
                  className="bg-white p-3 rounded-lg shadow-sm border border-gray-100"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="font-medium">Agence {agency.agency}</h5>
                    <span className="text-sm text-gray-500">
                      {total} contrats
                    </span>
                  </div>

                  <div className="flex h-4 rounded-full overflow-hidden mb-3">
                    {series.map((s) => {
                      const value = agency[s.dataKey];
                      const width = total > 0 ? (value / total) * 100 : 0;
                      return (
                        <div
                          key={s.dataKey}
                          className="h-full"
                          style={{
                            width: `${width}%`,
                            backgroundColor: s.color,
                            display: width > 0 ? "block" : "none",
                          }}
                          title={`${s.name}: ${value} (${Math.round(width)}%)`}
                        ></div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {series.map((s) => {
                      const value = agency[s.dataKey];
                      const percentage =
                        total > 0 ? Math.round((value / total) * 100) : 0;
                      if (percentage === 0) return null;

                      return (
                        <div
                          key={s.dataKey}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <div
                              className="w-2 h-2 rounded-full mr-1"
                              style={{ backgroundColor: s.color }}
                            ></div>
                            <span className="truncate">
                              {s.name.length > 15
                                ? s.name.substring(0, 15) + "..."
                                : s.name}
                            </span>
                          </div>
                          <span className="font-medium">{percentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg p-5 hover:shadow-lg transition-shadow col-span-1 lg:col-span-2">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          État des contrats
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode("global")}
            className={`px-3 py-1 rounded-md transition-colors ${
              viewMode === "global"
                ? "bg-indigo-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Vue globale
          </button>
          <button
            onClick={() => setViewMode("agency")}
            className={`px-3 py-1 rounded-md transition-colors ${
              viewMode === "agency"
                ? "bg-indigo-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Par agence
          </button>
        </div>
      </div>

      <div className="h-auto">
        {viewMode === "global" ? (
          // Vue globale: graphique en secteurs et en barres
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-96">
            {/* Graphique en secteurs */}
            <div className="h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.global}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {data.global.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    layout="horizontal"
                    align="center"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique en barres horizontal */}
            <div className="h-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sortedGlobalData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 160, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="value"
                    name="Nombre de contrats"
                    barSize={25}
                    radius={[0, 4, 4, 0]}
                  >
                    {sortedGlobalData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="right"
                      style={{ fill: "#333", fontSize: 12 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          // Vue par agence
          renderAgencyView()
        )}
      </div>

      {/* Résumé des totaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-indigo-50 p-3 rounded-lg border-l-4 border-indigo-500">
          <p className="text-xs text-indigo-600 font-medium">
            Contrats en cours
          </p>
          <p className="text-lg font-bold text-indigo-800">
            {totals.inProgress}
          </p>
        </div>
        <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
          <p className="text-xs text-red-600 font-medium">
            En cours avec dépassement
          </p>
          <p className="text-lg font-bold text-red-800">
            {totals.inProgressOverdue}
          </p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
          <p className="text-xs text-green-600 font-medium">
            Rachetés avec remise
          </p>
          <p className="text-lg font-bold text-green-800">
            {totals.redeemedWithDiscount}
          </p>
        </div>
        <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-500">
          <p className="text-xs text-yellow-600 font-medium">
            Rachetés sans remise
          </p>
          <p className="text-lg font-bold text-yellow-800">
            {totals.redeemedWithoutDiscount}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContractStatusChart;
